import { type PackSubmissionDetail, packKindSchema } from '@planuze/admin-contracts';
import { Card, CardContent, StatusBadge } from '@planuze/ui';
import type { ReactNode } from 'react';
import { usePackSubmissionsContent } from '../../content/sections/pack-submissions.js';
import { fmtBytes, fmtDateTime } from '../../lib/format.js';
import { CopyableId } from '../copyable-id.js';
import { kindLabel } from '../packs-catalog/pack-catalog-labels.js';
import {
  scanStatusLabel,
  scanStatusTone,
  submissionLabel,
  submissionTone,
} from './pack-submission-status.js';
import { PublisherLink } from './publisher-link.js';

function Field({
  label,
  children,
  wide,
}: {
  readonly label: string;
  readonly children: ReactNode;
  readonly wide?: boolean;
}) {
  return (
    <div className={wide ? 'flex flex-col gap-1 sm:col-span-2' : 'flex flex-col gap-1'}>
      <dt className="text-xs uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground">{children}</dd>
    </div>
  );
}

/* Badge distinguindo procedência da aprovação (ADR-0374/0462): auto-aprovado por fastpath
   (sem olhos humanos) vs revisado por staff. Só aparece quando já houve decisão. */
function ProvenanceBadge({ submission }: { readonly submission: PackSubmissionDetail }) {
  const copy = usePackSubmissionsContent();
  if (submission.fastpathApplied) {
    return <StatusBadge variant="warning">{copy.overview.fastpathBadge}</StatusBadge>;
  }
  if (submission.reviewedByStaffId !== undefined && submission.reviewedByStaffId !== null) {
    return <StatusBadge variant="info">{copy.overview.reviewedByStaffBadge}</StatusBadge>;
  }
  return null;
}

/* Badge do tipo do pack (ADR-0515). `kind` chega como string livre no DTO (coluna text nullable);
   valida contra o enum antes de traduzir e cai no valor cru se for desconhecido (fail-soft). */
function KindBadge({ kind }: { readonly kind: string }) {
  const parsed = packKindSchema.safeParse(kind);
  return (
    <StatusBadge variant="neutral">{parsed.success ? kindLabel(parsed.data) : kind}</StatusBadge>
  );
}

interface Props {
  readonly submission: PackSubmissionDetail;
}

export function SubmissionOverviewCard({ submission }: Props) {
  const copy = usePackSubmissionsContent();
  const o = copy.overview;
  const f = copy.fieldLabels;
  const {
    scanStatus,
    reviewedByStaffId,
    reviewedAt,
    reviewNotes,
    kind,
    fileBytes,
    fileHash,
    publisherFingerprint,
    scannedAt,
  } = submission;
  return (
    <Card>
      <CardContent className="pt-6">
        <dl className="grid gap-4 sm:grid-cols-2">
          <Field label={f.status}>
            <span className="inline-flex flex-wrap items-center gap-2">
              <StatusBadge variant={submissionTone(submission.status)} dot>
                {submissionLabel(submission.status, copy.submissionStatusLabels)}
              </StatusBadge>
              <ProvenanceBadge submission={submission} />
            </span>
          </Field>
          <Field label={f.scan}>
            {scanStatus !== undefined && scanStatus !== null ? (
              <StatusBadge variant={scanStatusTone(scanStatus)} dot>
                {scanStatusLabel(scanStatus, copy.scanStatusLabels)}
              </StatusBadge>
            ) : (
              <span className="text-muted-foreground">{o.notScannedLabel}</span>
            )}
          </Field>
          <Field label={f.publisher}>
            <PublisherLink publisher={submission.publisher} publisherId={submission.publisherId} />
          </Field>
          <Field label={f.version}>v{submission.version}</Field>
          {kind !== undefined && kind !== null && (
            <Field label={o.typeFieldLabel}>
              <KindBadge kind={kind} />
            </Field>
          )}
          <Field label={o.iterationsFieldLabel}>{submission.iterationCount}</Field>
          {fileBytes !== undefined && fileBytes !== null && (
            <Field label={o.sizeFieldLabel}>{fmtBytes(fileBytes)}</Field>
          )}
          <Field label={f.submittedAt}>{fmtDateTime(submission.submittedAt)}</Field>
          <Field label={o.scannedAtFieldLabel}>
            {scannedAt !== undefined && scannedAt !== null ? fmtDateTime(scannedAt) : '—'}
          </Field>
          <Field label={f.reviewedAt}>
            {reviewedAt !== undefined && reviewedAt !== null ? fmtDateTime(reviewedAt) : '—'}
          </Field>
          {reviewedByStaffId !== undefined && reviewedByStaffId !== null && (
            <Field label={o.reviewedByFieldLabel}>
              <CopyableId value={reviewedByStaffId} label={o.reviewerIdCopyLabel} />
            </Field>
          )}
          <Field label={f.pack}>
            <CopyableId value={submission.packId} label={o.packIdCopyLabel} />
          </Field>
          <Field label={o.submissionIdFieldLabel}>
            <CopyableId value={submission.id} label={o.submissionIdFieldLabel} />
          </Field>
          {fileHash !== undefined && fileHash !== null && (
            <Field label={o.fileHashFieldLabel}>
              <CopyableId value={fileHash} label={o.fileHashFieldLabel} />
            </Field>
          )}
          {publisherFingerprint !== undefined && publisherFingerprint !== null && (
            <Field label={o.fingerprintFieldLabel}>
              <CopyableId value={publisherFingerprint} label={o.fingerprintFieldLabel} />
            </Field>
          )}
          {reviewNotes !== undefined && reviewNotes !== null && (
            <Field label={o.reviewNotesFieldLabel} wide>
              {reviewNotes}
            </Field>
          )}
        </dl>
      </CardContent>
    </Card>
  );
}
